import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertCircle,
  BarChart3,
  Copy,
  Eye,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Settings,
  Square,
  Play,
  Trash2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useAuth } from "@/hooks/useAuth";
import {
  useAddMediaConnectOutput,
  useAwsMediaSummary,
  useCreateMediaConnectFlow,
  useCreateMediaLiveChannel,
  useDeleteMediaConnectFlow,
  useDeleteMediaLiveChannel,
  useMediaLiveConfigUpdateQueue,
  useMediaLiveChannelThumbnails,
  useMediaLiveInputDeviceThumbnail,
  useMediaLiveChannelConfig,
  useMediaConnectMetrics,
  useMediaConnectFlowAction,
  useMediaLiveChannelAction,
  useRemoveMediaConnectOutput,
  useUpdateMediaConnectOutput,
  useUpdateMediaLiveChannelConfig,
  type AddMediaConnectOutputInput,
  type AudioMappingInput,
  type AwsCloudWatchConnectionHealth,
  type AwsCloudWatchMetricHistory,
  type AwsCloudWatchMetricSnapshot,
  type AwsMediaConnectFlow,
  type AwsMediaConnectOutput,
  type AwsMediaLiveChannel,
  type AwsMediaLiveInput,
  type AwsMediaLiveInputDevice,
  type CreateMediaConnectFlowInput,
  type CreateMediaLiveChannelInput,
  type UpdateMediaConnectOutputInput,
  type UpdateMediaLiveChannelConfigInput,
} from "@/hooks/useAwsMedia";
import { ProjectLinkMenu } from "@/components/cloud-media/ProjectLinkMenu";
import { awsResourceKey } from "@/lib/aws-resource-links";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

type PendingRemoveOutput = {
  flowArn: string;
  outputArn: string;
  outputName: string;
} | null;

type PendingEditOutput = {
  flow: AwsMediaConnectFlow;
  output: AwsMediaConnectOutput;
} | null;

type PendingDeleteChannel = {
  channelId: string;
  channelName: string;
} | null;

type MetricsResource = {
  arn: string;
  type: "source" | "output";
  title: string;
} | null;

const DEFAULT_AUDIO_MAPPING: AudioMappingInput = {
  selectorName: "audio_1",
  selectorType: "track",
  track: 1,
  outputName: "audio_program",
  bitrate: 192000,
  sampleRate: 48000,
};

const FRAMERATE_OPTIONS: {
  label: string;
  numerator: number;
  denominator: number;
}[] = [
  { label: "23.976 fps", numerator: 24000, denominator: 1001 },
  { label: "24 fps", numerator: 24, denominator: 1 },
  { label: "25 fps", numerator: 25, denominator: 1 },
  { label: "29.97 fps", numerator: 30000, denominator: 1001 },
  { label: "30 fps", numerator: 30, denominator: 1 },
  { label: "50 fps", numerator: 50, denominator: 1 },
  { label: "59.94 fps", numerator: 60000, denominator: 1001 },
  { label: "60 fps", numerator: 60, denominator: 1 },
];

const MEDIACONNECT_OUTPUT_PROTOCOLS = [
  "srt-caller",
  "srt-listener",
  "zixi-push",
  "zixi-pull",
  "rist",
  "rtp",
] as const satisfies AddMediaConnectOutputInput["output"]["protocol"][];

type MediaConnectOutputProtocol =
  (typeof MEDIACONNECT_OUTPUT_PROTOCOLS)[number];
type EditableMediaConnectOutputSettings =
  | AddMediaConnectOutputInput["output"]
  | UpdateMediaConnectOutputInput["output"];

function isMediaConnectOutputProtocol(
  value: string | undefined,
): value is MediaConnectOutputProtocol {
  return MEDIACONNECT_OUTPUT_PROTOCOLS.some((protocol) => protocol === value);
}

function mediaConnectOutputFormFromOutput(
  output: AwsMediaConnectOutput,
): UpdateMediaConnectOutputInput["output"] {
  return {
    protocol: isMediaConnectOutputProtocol(output.protocol)
      ? output.protocol
      : "srt-listener",
    destination: output.destination,
    port: output.port,
    streamId: output.streamId,
  };
}

function sanitizeMediaConnectOutputSettings<
  T extends EditableMediaConnectOutputSettings,
>(output: T): T {
  return {
    ...output,
    destination:
      output.protocol !== "srt-listener" ? output.destination : undefined,
    streamId: output.streamId || undefined,
  };
}

function mediaConnectOutputUrl(output: AwsMediaConnectOutput) {
  if (
    output.protocol === "srt-listener" &&
    output.listenerAddress &&
    output.port
  ) {
    return `srt://${output.listenerAddress}:${output.port}`;
  }

  return `${output.protocol ?? "-"}${
    output.destination ? ` -> ${output.destination}` : ""
  }${output.port ? `:${output.port}` : ""}`;
}

export default function CloudMediaPage() {
  return (
    <div className="space-y-6" data-testid="cloud-media-page">
      <CloudMediaManager showHeader layout="tabs" showCreate />
    </div>
  );
}

export function CloudMediaManager({
  filterKeys,
  layout = "tabs",
  showCreate = true,
  showHeader = true,
  emptyMessage = "No AWS resources linked yet.",
  projectId,
}: {
  filterKeys?: Set<string>;
  layout?: "tabs" | "stacked";
  showCreate?: boolean;
  showHeader?: boolean;
  emptyMessage?: string;
  projectId?: string;
}) {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canManageAws = role === "admin" || role === "employee";
  const [isCreateFlowOpen, setIsCreateFlowOpen] = useState(false);
  const [isAddOutputOpen, setIsAddOutputOpen] = useState(false);
  const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<AwsMediaConnectFlow | null>(
    null,
  );
  const [detailResource, setDetailResource] = useState<{
    title: string;
    data: unknown;
  } | null>(null);
  const [metricsResource, setMetricsResource] = useState<MetricsResource>(null);
  const [configChannel, setConfigChannel] =
    useState<AwsMediaLiveChannel | null>(null);
  const [pendingRemoveOutput, setPendingRemoveOutput] =
    useState<PendingRemoveOutput>(null);
  const [pendingEditOutput, setPendingEditOutput] =
    useState<PendingEditOutput>(null);
  const [pendingDeleteChannel, setPendingDeleteChannel] =
    useState<PendingDeleteChannel>(null);
  const [pendingDeleteFlow, setPendingDeleteFlow] = useState<{
    flowArn: string;
    flowName: string;
  } | null>(null);

  const { data, isLoading, isError, error, isFetching } = useAwsMediaSummary();
  const flowAction = useMediaConnectFlowAction();
  const removeOutput = useRemoveMediaConnectOutput();
  const channelAction = useMediaLiveChannelAction();
  const deleteChannel = useDeleteMediaLiveChannel();
  const deleteFlow = useDeleteMediaConnectFlow();

  const allFlows = data?.mediaConnect.flows ?? [];
  const allChannels = data?.mediaLive.channels ?? [];
  const allInputs = data?.mediaLive.inputs ?? [];
  const allInputDevices = data?.mediaLive.inputDevices ?? [];

  const flows = filterKeys
    ? allFlows.filter(
        (flow) =>
          filterKeys.has(
            awsResourceKey({ type: "mediaconnect_flow", arn: flow.arn }),
          ) ||
          flow.outputs.some((output) =>
            filterKeys.has(
              awsResourceKey({ type: "mediaconnect_output", arn: output.arn }),
            ),
          ),
      )
    : allFlows;
  const channels = filterKeys
    ? allChannels.filter((channel) =>
        filterKeys.has(
          awsResourceKey({
            type: "medialive_channel",
            arn: channel.arn,
            id: channel.id,
          }),
        ),
      )
    : allChannels;
  const inputs = filterKeys
    ? allInputs.filter((input) =>
        filterKeys.has(
          awsResourceKey({
            type: "medialive_input",
            arn: input.arn,
            id: input.id,
          }),
        ),
      )
    : allInputs;
  const inputDevices = filterKeys
    ? allInputDevices.filter((device) =>
        filterKeys.has(
          awsResourceKey({
            type: "medialive_input_device",
            arn: device.arn,
            id: device.id,
          }),
        ),
      )
    : allInputDevices;

  const isFiltered = Boolean(filterKeys);
  const hasAnyResource =
    flows.length > 0 ||
    channels.length > 0 ||
    inputs.length > 0 ||
    inputDevices.length > 0;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["aws-media", "summary"] });
  };

  const confirmRemoveOutput = () => {
    if (!pendingRemoveOutput) return;
    removeOutput.mutate(pendingRemoveOutput, {
      onSuccess: () => setPendingRemoveOutput(null),
    });
  };

  const confirmDeleteChannel = () => {
    if (!pendingDeleteChannel) return;
    deleteChannel.mutate(pendingDeleteChannel.channelId, {
      onSuccess: () => setPendingDeleteChannel(null),
    });
  };

  const flowsCard = (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Flows</CardTitle>
        {canManageAws && showCreate && (
          <Button onClick={() => setIsCreateFlowOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Flow
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <MediaConnectTable
          flows={flows}
          isLoading={isLoading}
          isAdmin={canManageAws}
          projectId={projectId}
          isPending={flowAction.isPending}
          onStart={(flowArn) => flowAction.mutate({ flowArn, action: "start" })}
          onStop={(flowArn) => flowAction.mutate({ flowArn, action: "stop" })}
          onAddOutput={(flow) => {
            setSelectedFlow(flow);
            setIsAddOutputOpen(true);
          }}
          onRemoveOutput={(input) => setPendingRemoveOutput(input)}
          onEditOutput={(input) => setPendingEditOutput(input)}
          onShowDetails={(flow) =>
            setDetailResource({
              title: `MediaConnect Flow: ${flow.name || "Unnamed"}`,
              data: flow,
            })
          }
          onShowMetrics={(resource) => setMetricsResource(resource)}
          onDeleteFlow={(flow) =>
            setPendingDeleteFlow({
              flowArn: flow.arn,
              flowName: flow.name || "Unnamed",
            })
          }
        />
      </CardContent>
    </Card>
  );

  const channelsCard = (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Channels</CardTitle>
        {canManageAws && showCreate && (
          <Button onClick={() => setIsCreateChannelOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Channel
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <MediaLiveChannelTable
          channels={channels}
          isLoading={isLoading}
          isAdmin={canManageAws}
          projectId={projectId}
          isPending={channelAction.isPending || deleteChannel.isPending}
          onStart={(channelId) =>
            channelAction.mutate({ channelId, action: "start" })
          }
          onStop={(channelId) =>
            channelAction.mutate({ channelId, action: "stop" })
          }
          onDelete={(channel) =>
            setPendingDeleteChannel({
              channelId: channel.id,
              channelName: channel.name ?? channel.id,
            })
          }
          onConfig={(channel) => setConfigChannel(channel)}
          onShowDetails={(channel) =>
            setDetailResource({
              title: `MediaLive Channel: ${channel.name ?? channel.id}`,
              data: channel,
            })
          }
        />
      </CardContent>
    </Card>
  );

  const inputsRow = (
    <div className="grid gap-4 lg:grid-cols-2">
      <MediaLiveInputsCard
        inputs={inputs}
        isLoading={isLoading}
        onShowDetails={(input) =>
          setDetailResource({
            title: `MediaLive Input: ${input.name ?? input.id}`,
            data: input,
          })
        }
      />
      <InputDevicesCard
        devices={inputDevices}
        isLoading={isLoading}
        onShowDetails={(device) =>
          setDetailResource({
            title: `Elemental Link: ${device.name ?? device.id}`,
            data: device,
          })
        }
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {showHeader && (
        <PageHeader title="Cloud Media">
          <Button variant="outline" onClick={refresh} disabled={isFetching}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </PageHeader>
      )}

      {!canManageAws && (
        <Badge variant="secondary">Read-only AWS media access</Badge>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>AWS media unavailable</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : "Failed to load AWS media"}
          </AlertDescription>
        </Alert>
      )}

      {layout === "tabs" ? (
        <Tabs defaultValue="mediaconnect">
          <TabsList>
            <TabsTrigger value="mediaconnect">MediaConnect</TabsTrigger>
            <TabsTrigger value="medialive">MediaLive</TabsTrigger>
            <TabsTrigger value="topology">Topology</TabsTrigger>
          </TabsList>

          <TabsContent value="mediaconnect" className="mt-4">
            {flowsCard}
          </TabsContent>

          <TabsContent value="medialive" className="mt-4 space-y-4">
            {channelsCard}
            {inputsRow}
          </TabsContent>

          <TabsContent value="topology" className="mt-4">
            <TopologyCard flows={flows} channels={channels} />
          </TabsContent>
        </Tabs>
      ) : isLoading ? (
        <div className="text-sm text-muted-foreground">
          Loading cloud resources...
        </div>
      ) : isFiltered && !hasAnyResource ? (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-4">
          {flows.length > 0 && flowsCard}
          {channels.length > 0 && channelsCard}
          {(inputs.length > 0 || inputDevices.length > 0) && inputsRow}
        </div>
      )}

      <CreateFlowDialog
        open={isCreateFlowOpen}
        onOpenChange={setIsCreateFlowOpen}
      />
      <AddOutputDialog
        flow={selectedFlow}
        open={isAddOutputOpen}
        onOpenChange={(open) => {
          setIsAddOutputOpen(open);
          if (!open) setSelectedFlow(null);
        }}
      />
      <EditOutputDialog
        pendingOutput={pendingEditOutput}
        open={!!pendingEditOutput}
        onOpenChange={(open) => !open && setPendingEditOutput(null)}
      />
      <CreateChannelDialog
        open={isCreateChannelOpen}
        onOpenChange={setIsCreateChannelOpen}
        flows={allFlows}
        inputs={allInputs}
        inputDevices={allInputDevices}
      />
      <ResourceDetailDialog
        resource={detailResource}
        onOpenChange={(open) => !open && setDetailResource(null)}
      />
      <MediaConnectMetricsDialog
        resource={metricsResource}
        onOpenChange={(open) => !open && setMetricsResource(null)}
      />
      <ChannelConfigDialog
        channel={configChannel}
        flows={allFlows}
        isAdmin={canManageAws}
        open={!!configChannel}
        onOpenChange={(open) => !open && setConfigChannel(null)}
      />

      <AlertDialog
        open={!!pendingRemoveOutput}
        onOpenChange={(open) => !open && setPendingRemoveOutput(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove MediaConnect output?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the AWS output{" "}
              <span className="font-medium">
                {pendingRemoveOutput?.outputName}
              </span>{" "}
              from the flow. It does not delete the flow.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveOutput}
              disabled={removeOutput.isPending}
            >
              Remove Output
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingDeleteChannel}
        onOpenChange={(open) => !open && setPendingDeleteChannel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MediaLive channel?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the AWS channel{" "}
              <span className="font-medium">
                {pendingDeleteChannel?.channelName}
              </span>
              . The channel must be idle before AWS will delete it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteChannel}
              disabled={deleteChannel.isPending}
            >
              Delete Channel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingDeleteFlow}
        onOpenChange={(open) => !open && setPendingDeleteFlow(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MediaConnect flow?</AlertDialogTitle>
            <AlertDialogDescription>
              The flow{" "}
              <span className="font-medium">{pendingDeleteFlow?.flowName}</span>{" "}
              will be permanently deleted. The flow must be stopped before it
              can be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingDeleteFlow) return;
                deleteFlow.mutate(pendingDeleteFlow.flowArn, {
                  onSuccess: () => setPendingDeleteFlow(null),
                });
              }}
              disabled={deleteFlow.isPending}
            >
              Delete Flow
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MediaConnectTable({
  flows,
  isLoading,
  isAdmin,
  projectId,
  isPending,
  onStart,
  onStop,
  onAddOutput,
  onRemoveOutput,
  onEditOutput,
  onShowDetails,
  onShowMetrics,
  onDeleteFlow,
}: {
  flows: AwsMediaConnectFlow[];
  isLoading: boolean;
  isAdmin: boolean;
  projectId?: string;
  isPending: boolean;
  onStart: (flowArn: string) => void;
  onStop: (flowArn: string) => void;
  onAddOutput: (flow: AwsMediaConnectFlow) => void;
  onRemoveOutput: (input: Exclude<PendingRemoveOutput, null>) => void;
  onEditOutput: (input: Exclude<PendingEditOutput, null>) => void;
  onShowDetails: (flow: AwsMediaConnectFlow) => void;
  onShowMetrics: (resource: Exclude<MetricsResource, null>) => void;
  onDeleteFlow: (flow: AwsMediaConnectFlow) => void;
}) {
  if (isLoading) return <EmptyTableMessage message="Loading flows..." />;
  if (flows.length === 0) return <EmptyTableMessage message="No flows found" />;

  return (
    <div className="space-y-3">
      {flows.map((flow) => (
        <div key={flow.arn} className="rounded-lg border p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium">{flow.name || "-"}</div>
              <div className="text-xs text-muted-foreground">
                {flow.availabilityZone ?? "No availability zone"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge value={flow.status} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Source
              </div>
              <div className="text-sm">
                {flow.source?.protocol ?? "-"}
                {flow.source?.ingestIp && flow.source.ingestPort
                  ? ` / ${flow.source.ingestIp}:${flow.source.ingestPort}`
                  : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                {flow.source?.name ?? "No source"} / 0.0.0.0/0
              </div>
              {flow.source?.peerIpAddress && (
                <div className="text-xs text-muted-foreground">
                  Peer {flow.source.peerIpAddress}
                </div>
              )}
              {flow.source?.healthStatus && (
                <div className="mt-1 flex items-center gap-2">
                  <ConnectionHealthBadge health={flow.source.healthStatus} />
                  {flow.source.arn && (
                    <IconButton
                      label="Source metrics"
                      onClick={() =>
                        onShowMetrics({
                          arn: flow.source!.arn!,
                          type: "source",
                          title: `${flow.name} source`,
                        })
                      }
                    >
                      <BarChart3 className="h-4 w-4" />
                    </IconButton>
                  )}
                </div>
              )}
              <MetricSnapshotList
                metrics={flow.source?.metrics ?? []}
                names={[
                  "SourceBitRate",
                  "SourcePacketLossPercent",
                  "SourceDroppedPackets",
                  "SourceLatency",
                ]}
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Outputs
              </div>
              {flow.outputs.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No outputs
                </span>
              ) : (
                <div className="space-y-2">
                  {flow.outputs.map((output) => (
                    <div
                      key={output.arn}
                      className="flex items-start justify-between gap-3 rounded-md border px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="truncate text-sm font-medium">
                            {output.name}
                          </span>
                          {output.healthStatus && (
                            <ConnectionHealthBadge
                              health={output.healthStatus}
                            />
                          )}
                          <IconButton
                            label="Output metrics"
                            onClick={() =>
                              onShowMetrics({
                                arn: output.arn,
                                type: "output",
                                title: `${flow.name} / ${output.name}`,
                              })
                            }
                          >
                            <BarChart3 className="h-4 w-4" />
                          </IconButton>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {mediaConnectOutputUrl(output)}
                        </div>
                        {output.peerIpAddress && (
                          <div className="text-xs text-muted-foreground">
                            Peer {output.peerIpAddress}
                          </div>
                        )}
                        <MetricSnapshotList
                          metrics={output.metrics}
                          names={[
                            "OutputBitRate",
                            "OutputLatency",
                            "OutputRoundTripTime",
                            "OutputDisconnections",
                          ]}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        {isAdmin && (
                          <>
                            <IconButton
                              label="Edit output settings"
                              disabled={isPending}
                              onClick={() => onEditOutput({ flow, output })}
                            >
                              <Pencil className="h-4 w-4" />
                            </IconButton>
                            <IconButton
                              label="Remove output"
                              disabled={isPending}
                              onClick={() =>
                                onRemoveOutput({
                                  flowArn: flow.arn,
                                  outputArn: output.arn,
                                  outputName: output.name,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </IconButton>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <IconButton
              label="Flow details"
              onClick={() => onShowDetails(flow)}
            >
              <Eye className="h-4 w-4" />
            </IconButton>
            {isAdmin && (
              <>
                <Button
                  size="sm"
                  disabled={isPending || flow.status === "ACTIVE"}
                  onClick={() => onStart(flow.arn)}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isPending || flow.status === "STANDBY"}
                  onClick={() => onStop(flow.arn)}
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
                <Button size="sm" onClick={() => onAddOutput(flow)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Output
                </Button>
                <IconButton
                  label="Delete flow"
                  disabled={isPending}
                  onClick={() => onDeleteFlow(flow)}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </>
            )}
            <ProjectLinkMenu
              projectId={projectId}
              resource={{
                type: "mediaconnect_flow",
                id: flow.arn,
                arn: flow.arn,
                name: flow.name,
                status: flow.status,
                resource: flow,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaLiveChannelTable({
  channels,
  isLoading,
  isAdmin,
  projectId,
  isPending,
  onStart,
  onStop,
  onDelete,
  onConfig,
  onShowDetails,
}: {
  channels: AwsMediaLiveChannel[];
  isLoading: boolean;
  isAdmin: boolean;
  projectId?: string;
  isPending: boolean;
  onStart: (channelId: string) => void;
  onStop: (channelId: string) => void;
  onDelete: (channel: AwsMediaLiveChannel) => void;
  onConfig: (channel: AwsMediaLiveChannel) => void;
  onShowDetails: (channel: AwsMediaLiveChannel) => void;
}) {
  if (isLoading) return <EmptyTableMessage message="Loading channels..." />;
  if (channels.length === 0)
    return <EmptyTableMessage message="No MediaLive channels found" />;

  return (
    <div className="space-y-3">
      {channels.map((channel) => (
        <div key={channel.id} className="rounded-lg border p-4 space-y-3">
          <div className="flex flex-wrap items-start gap-3">
            <ChannelThumbnail channelId={channel.id} state={channel.state} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  {channel.name ?? channel.id}
                </span>
                <StatusBadge value={channel.state ?? "UNKNOWN"} />
              </div>
              <div className="text-xs text-muted-foreground">
                ID {channel.id}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {channel.pipelinesRunningCount ?? 0} pipeline(s)
              </div>
              <MediaLiveConfigUpdateStatus channelId={channel.id} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Inputs
              </div>
              {channel.inputAttachments.length === 0 ? (
                <span className="text-sm text-muted-foreground">None</span>
              ) : (
                channel.inputAttachments.map((input) => (
                  <div
                    key={`${channel.id}-${input.inputId}`}
                    className="text-sm"
                  >
                    {input.name ?? "Input"} / {input.inputId}
                  </div>
                ))
              )}
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Destinations
              </div>
              {channel.destinations.flatMap((dest) => dest.urls).length ===
              0 ? (
                <span className="text-sm text-muted-foreground">None</span>
              ) : (
                channel.destinations.map((destination) => (
                  <div
                    key={`${channel.id}-${destination.id}`}
                    className="mb-2 space-y-1 text-sm last:mb-0"
                  >
                    <div className="break-all">
                      {destination.urls.join(", ")}
                    </div>
                    {destination.id && (
                      <div className="text-xs text-muted-foreground">
                        {destination.id}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <IconButton
              label="Channel details"
              onClick={() => onShowDetails(channel)}
            >
              <Eye className="h-4 w-4" />
            </IconButton>
            <IconButton
              label="View and update config"
              onClick={() => onConfig(channel)}
            >
              <Settings className="h-4 w-4" />
            </IconButton>
            {isAdmin && (
              <>
                <IconButton
                  label="Start channel"
                  disabled={isPending || channel.state === "RUNNING"}
                  onClick={() => onStart(channel.id)}
                >
                  <Play className="h-4 w-4" />
                </IconButton>
                <IconButton
                  label="Stop channel"
                  disabled={isPending || channel.state === "IDLE"}
                  onClick={() => onStop(channel.id)}
                >
                  <Square className="h-4 w-4" />
                </IconButton>
                <IconButton
                  label="Delete channel"
                  disabled={isPending || channel.state !== "IDLE"}
                  onClick={() => onDelete(channel)}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </>
            )}
            <ProjectLinkMenu
              projectId={projectId}
              resource={{
                type: "medialive_channel",
                id: channel.id,
                arn: channel.arn,
                name: channel.name,
                status: channel.state,
                resource: channel,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaLiveInputsCard({
  inputs,
  isLoading,
  onShowDetails,
}: {
  inputs: AwsMediaLiveInput[];
  isLoading: boolean;
  onShowDetails: (input: AwsMediaLiveInput) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Inputs</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyTableMessage message="Loading inputs..." />
        ) : inputs.length === 0 ? (
          <EmptyTableMessage message="No MediaLive inputs found" />
        ) : (
          <div className="space-y-2">
            {inputs.map((input) => (
              <div
                key={input.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="font-medium">{input.name ?? input.id}</div>
                  <div className="text-xs text-muted-foreground">
                    ID {input.id}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Device:{" "}
                    {input.inputDevices.map((device) => device.id).join(", ") ||
                      "-"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={input.state ?? "UNKNOWN"} />
                  <IconButton
                    label="Input details"
                    onClick={() => onShowDetails(input)}
                  >
                    <Eye className="h-4 w-4" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatQueueTimestamp(value?: string) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function MediaLiveConfigUpdateStatus({ channelId }: { channelId: string }) {
  const { data, isFetching } = useMediaLiveConfigUpdateQueue(channelId);
  const queue = data?.queue;

  if (!queue) {
    return isFetching ? (
      <div className="mt-2 text-xs text-muted-foreground">
        Checking update status
      </div>
    ) : null;
  }

  if (
    !queue.lastError &&
    !queue.activeOperationId &&
    queue.pendingCount === 0
  ) {
    return null;
  }

  const value = queue.lastError
    ? "FAILED"
    : queue.activeOperationId
      ? "APPLYING"
      : queue.pendingCount > 0
        ? "QUEUED"
        : "IDLE";

  const detail = queue.lastError
    ? queue.lastError
    : queue.activeOperationId
      ? `Active ${queue.activeOperationId.slice(0, 8)}`
      : queue.pendingCount > 0
        ? `${queue.pendingCount} pending`
        : `Applied ${formatQueueTimestamp(queue.lastAppliedAt)}`;
  const isWorking = Boolean(queue.activeOperationId || queue.pendingCount > 0);

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-2">
        {isWorking && (
          <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
        <span className="text-xs text-muted-foreground">Update</span>
        <StatusBadge value={value} />
      </div>
      <div
        className={
          queue.lastError
            ? "max-w-60 truncate text-xs text-destructive"
            : "max-w-60 truncate text-xs text-muted-foreground"
        }
        title={detail}
      >
        {detail}
      </div>
    </div>
  );
}

function InputDevicesCard({
  devices,
  isLoading,
  onShowDetails,
}: {
  devices: AwsMediaLiveInputDevice[];
  isLoading: boolean;
  onShowDetails: (device: AwsMediaLiveInputDevice) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Elemental Link Devices</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyTableMessage message="Loading input devices..." />
        ) : devices.length === 0 ? (
          <EmptyTableMessage message="No Elemental Link devices found" />
        ) : (
          <div className="space-y-2">
            {devices.map((device) => (
              <div
                key={device.id}
                className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2"
              >
                <InputDeviceThumbnail
                  inputDeviceId={device.id}
                  connectionState={device.connectionState}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{device.name ?? device.id}</div>
                  <div className="text-xs text-muted-foreground">
                    ID {device.id}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {device.type ?? "-"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={device.connectionState ?? "UNKNOWN"} />
                  <IconButton
                    label="Device details"
                    onClick={() => onShowDetails(device)}
                  >
                    <Eye className="h-4 w-4" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TopologyCard({
  flows,
  channels,
}: {
  flows: AwsMediaConnectFlow[];
  channels: AwsMediaLiveChannel[];
}) {
  const rows = useMemo(() => {
    return channels.map((channel) => {
      const destinationUrls = channel.destinations.flatMap((dest) => dest.urls);
      const linkedFlow = flows.find((flow) =>
        destinationUrls.some((url) =>
          flow.source?.ingestIp && flow.source.ingestPort
            ? url.includes(`${flow.source.ingestIp}:${flow.source.ingestPort}`)
            : false,
        ),
      );
      return { channel, destinationUrls, linkedFlow };
    });
  }, [channels, flows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pipeline Topology</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyTableMessage message="No MediaLive channels to map yet" />
        ) : (
          <div className="space-y-3">
            {rows.map(({ channel, destinationUrls, linkedFlow }) => (
              <div
                key={channel.id}
                className="grid gap-3 rounded-lg border p-4 grid-cols-2 md:grid-cols-4"
              >
                <TopologyNode
                  label="MediaLive Channel"
                  title={channel.name ?? channel.id}
                  status={channel.state ?? "UNKNOWN"}
                />
                <TopologyNode
                  label="Destination"
                  title={destinationUrls.join(", ") || "No destination"}
                  status={destinationUrls.length ? "CONFIGURED" : "MISSING"}
                />
                <TopologyNode
                  label="MediaConnect Flow"
                  title={linkedFlow?.name ?? "No detected flow"}
                  status={linkedFlow?.status ?? "UNLINKED"}
                />
                <TopologyNode
                  label="Outputs"
                  title={
                    linkedFlow
                      ? `${linkedFlow.outputs.length} configured`
                      : "No outputs"
                  }
                  status={linkedFlow ? "VISIBLE" : "UNKNOWN"}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TopologyNode({
  label,
  title,
  status,
}: {
  label: string;
  title: string;
  status: string;
}) {
  return (
    <div className="min-w-0 rounded-md bg-muted/40 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-medium">{title}</div>
      <div className="mt-2">
        <StatusBadge value={status} />
      </div>
    </div>
  );
}

function ThumbnailFrame({
  dataUrl,
  alt,
  isLoading,
}: {
  dataUrl?: string;
  alt: string;
  isLoading: boolean;
}) {
  return (
    <div className="flex h-16 w-28 items-center justify-center overflow-hidden rounded-md border bg-muted text-xs text-muted-foreground">
      {dataUrl ? (
        <img
          src={dataUrl}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <span>{isLoading ? "Loading" : "No preview"}</span>
      )}
    </div>
  );
}

function ChannelThumbnail({
  channelId,
  state,
}: {
  channelId: string;
  state?: string;
}) {
  const canLoad = state === "RUNNING";
  const thumbnails = useMediaLiveChannelThumbnails(channelId, canLoad);
  const thumbnail = thumbnails.data?.thumbnails[0];
  return (
    <ThumbnailFrame
      dataUrl={thumbnail?.dataUrl}
      alt={`MediaLive channel ${channelId} preview`}
      isLoading={canLoad && thumbnails.isLoading}
    />
  );
}

function InputDeviceThumbnail({
  inputDeviceId,
  connectionState,
}: {
  inputDeviceId: string;
  connectionState?: string;
}) {
  const canLoad = connectionState === "CONNECTED";
  const thumbnail = useMediaLiveInputDeviceThumbnail(inputDeviceId, canLoad);
  return (
    <ThumbnailFrame
      dataUrl={thumbnail.data?.dataUrl}
      alt={`Input device ${inputDeviceId} preview`}
      isLoading={canLoad && thumbnail.isLoading}
    />
  );
}

function formatMetricValue(metric: AwsCloudWatchMetricSnapshot) {
  if (metric.value === undefined) return "-";
  if (metric.metricName.endsWith("BitRate")) {
    return `${(metric.value / 1_000_000).toFixed(1)}M`;
  }
  if (metric.unit === "%") {
    return `${metric.value.toFixed(1)}%`;
  }
  if (metric.unit === "ms") {
    return `${metric.value.toFixed(0)}ms`;
  }
  return Number.isInteger(metric.value)
    ? String(metric.value)
    : metric.value.toFixed(2);
}

function shortMetricLabel(metricName: string) {
  switch (metricName) {
    case "SourceBitRate":
    case "OutputBitRate":
      return "BR";
    case "SourcePacketLossPercent":
      return "Loss";
    case "SourceDroppedPackets":
      return "Drop";
    case "SourceLatency":
    case "OutputLatency":
      return "Lat";
    case "OutputRoundTripTime":
      return "RTT";
    case "SourceDisconnections":
    case "OutputDisconnections":
      return "Disc";
    default:
      return metricName;
  }
}

function metricDescription(metricName: string) {
  switch (metricName) {
    case "SourceBitRate":
    case "OutputBitRate":
      return "BR — Bitrate (Mbps)";
    case "SourcePacketLossPercent":
      return "Loss — Packet loss (%)";
    case "SourceDroppedPackets":
      return "Drop — Dropped packets";
    case "SourceLatency":
    case "OutputLatency":
      return "Lat — Latency (ms)";
    case "OutputRoundTripTime":
      return "RTT — Round-trip time (ms)";
    case "SourceDisconnections":
    case "OutputDisconnections":
      return "Disc — Disconnections";
    default:
      return metricName;
  }
}

function MetricSnapshotList({
  metrics,
  names,
}: {
  metrics: AwsCloudWatchMetricSnapshot[];
  names: string[];
}) {
  const visibleMetrics = names
    .map((name) => metrics.find((metric) => metric.metricName === name))
    .filter((metric): metric is AwsCloudWatchMetricSnapshot => Boolean(metric));

  if (visibleMetrics.length === 0) return null;

  const hasAnyValue = visibleMetrics.some(
    (metric) => metric.value !== undefined,
  );

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      {visibleMetrics.map((metric) => (
        <Tooltip key={metric.metricName}>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="h-5 px-1.5 text-[10px] font-normal leading-none"
            >
              {shortMetricLabel(metric.metricName)} {formatMetricValue(metric)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <div>{metricDescription(metric.metricName)}</div>
              <div>
                {metric.label}: {formatMetricValue(metric)}
              </div>
              <div>{metric.stat}</div>
              {metric.timestamp && (
                <div>{new Date(metric.timestamp).toLocaleTimeString()}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      ))}
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="h-5 gap-1 px-1.5 text-[10px] font-normal leading-none text-muted-foreground"
          >
            <Info className="h-3 w-3" />
            {hasAnyValue ? "Legend" : "No live data"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            {!hasAnyValue && (
              <div className="font-medium">
                No recent CloudWatch datapoints — the resource may be idle or
                not receiving a stream.
              </div>
            )}
            {visibleMetrics.map((metric) => (
              <div key={metric.metricName}>
                {metricDescription(metric.metricName)}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

const mediaConnectMetricChartConfig = {
  value: {
    label: "Value",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

function MediaConnectMetricsDialog({
  resource,
  onOpenChange,
}: {
  resource: MetricsResource;
  onOpenChange: (open: boolean) => void;
}) {
  const metrics = useMediaConnectMetrics(resource?.arn, resource?.type, 60);
  const visibleMetrics =
    metrics.data?.metrics.filter((metric) => metric.points.length > 0) ?? [];

  return (
    <Dialog open={!!resource} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{resource?.title ?? "MediaConnect Metrics"}</DialogTitle>
        </DialogHeader>
        {metrics.isLoading ? (
          <EmptyTableMessage message="Loading metrics..." />
        ) : metrics.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Metrics unavailable</AlertTitle>
            <AlertDescription>
              {metrics.error instanceof Error
                ? metrics.error.message
                : "Failed to load CloudWatch metrics"}
            </AlertDescription>
          </Alert>
        ) : visibleMetrics.length === 0 ? (
          <EmptyTableMessage message="No CloudWatch datapoints in the last hour" />
        ) : (
          <div className="grid max-h-[72vh] gap-4 overflow-y-auto pr-2 lg:grid-cols-2">
            {visibleMetrics.map((metric) => (
              <MetricHistoryChart key={metric.metricName} metric={metric} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MetricHistoryChart({
  metric,
}: {
  metric: AwsCloudWatchMetricHistory;
}) {
  const chartData = metric.points.map((point) => ({
    time: new Date(point.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    value: metric.metricName.endsWith("BitRate")
      ? point.value / 1_000_000
      : point.value,
  }));
  const unit = metric.metricName.endsWith("BitRate") ? "Mbps" : metric.unit;

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{metric.label}</div>
          <div className="text-xs text-muted-foreground">
            {metric.metricName} / {metric.stat}
          </div>
        </div>
        <Badge variant="outline">{formatMetricValue(metric)}</Badge>
      </div>
      <ChartContainer
        config={mediaConnectMetricChartConfig}
        className="h-[220px] w-full"
      >
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" tick={{ fontSize: 11 }} minTickGap={24} />
          <YAxis tick={{ fontSize: 11 }} width={52} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) =>
                  `${typeof value === "number" ? value.toFixed(2) : value}${
                    unit ? ` ${unit}` : ""
                  }`
                }
              />
            }
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--color-value)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
    </div>
  );
}

function ResourceDetailDialog({
  resource,
  onOpenChange,
}: {
  resource: { title: string; data: unknown } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const json = resource ? JSON.stringify(resource.data, null, 2) : "";

  return (
    <Dialog open={!!resource} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{resource?.title ?? "Resource Details"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigator.clipboard?.writeText(json)}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy JSON
            </Button>
          </div>
          <Textarea
            readOnly
            value={json}
            className="min-h-[420px] font-mono text-xs"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

type ChannelConfigForm = UpdateMediaLiveChannelConfigInput["config"];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value
        .map((item) => asRecord(item))
        .filter((item): item is Record<string, unknown> => Boolean(item))
    : [];
}

function asString(value: unknown) {
  return typeof value === "string" ? value : undefined;
}

function asNumber(value: unknown) {
  return typeof value === "number" ? value : undefined;
}

function readNestedRecord(
  source: Record<string, unknown> | null,
  keys: string[],
): Record<string, unknown> | null {
  return keys.reduce<Record<string, unknown> | null>((current, key) => {
    if (!current) return null;
    return asRecord(current[key]);
  }, source);
}

function inferAudioMapping(
  description: Record<string, unknown>,
  selectorByName: Map<string, Record<string, unknown>>,
  index: number,
): AudioMappingInput {
  const selectorName =
    asString(description.AudioSelectorName) || `audio_${index + 1}`;
  const selector = selectorByName.get(selectorName);
  const selectorSettings = asRecord(selector?.SelectorSettings);
  const trackSelection = asRecord(selectorSettings?.AudioTrackSelection);
  const pidSelection = asRecord(selectorSettings?.AudioPidSelection);
  const languageSelection = asRecord(selectorSettings?.AudioLanguageSelection);
  const firstTrack = asRecordArray(trackSelection?.Tracks)[0];
  const aacSettings = readNestedRecord(description, [
    "CodecSettings",
    "AacSettings",
  ]);

  if (pidSelection?.Pid) {
    return {
      selectorName,
      selectorType: "pid",
      pid: asNumber(pidSelection.Pid) ?? 1,
      outputName: asString(description.Name) || `audio_${index + 1}`,
      bitrate: asNumber(aacSettings?.Bitrate) ?? 192000,
      sampleRate: asNumber(aacSettings?.SampleRate) ?? 48000,
    };
  }

  if (languageSelection?.LanguageCode) {
    return {
      selectorName,
      selectorType: "language",
      languageCode: asString(languageSelection.LanguageCode) ?? "eng",
      outputName: asString(description.Name) || `audio_${index + 1}`,
      bitrate: asNumber(aacSettings?.Bitrate) ?? 192000,
      sampleRate: asNumber(aacSettings?.SampleRate) ?? 48000,
    };
  }

  return {
    selectorName,
    selectorType: "track",
    track: asNumber(firstTrack?.Track) ?? 1,
    outputName: asString(description.Name) || `audio_${index + 1}`,
    bitrate: asNumber(aacSettings?.Bitrate) ?? 192000,
    sampleRate: asNumber(aacSettings?.SampleRate) ?? 48000,
  };
}

function formFromChannelConfig(
  channel: AwsMediaLiveChannel,
  config: Record<string, unknown> | null,
): ChannelConfigForm {
  const encoderSettings = asRecord(config?.EncoderSettings);
  const firstVideo = asRecordArray(encoderSettings?.VideoDescriptions)[0];
  const h264Settings = readNestedRecord(firstVideo, [
    "CodecSettings",
    "H264Settings",
  ]);
  const firstDestination = asRecordArray(config?.Destinations)[0];
  const firstSetting = asRecordArray(firstDestination?.Settings)[0];
  const firstInputAttachment = asRecordArray(config?.InputAttachments)[0];
  const inputSettings = asRecord(firstInputAttachment?.InputSettings);
  const selectors = asRecordArray(inputSettings?.AudioSelectors);
  const selectorByName = new Map(
    selectors
      .map((selector) => [asString(selector.Name), selector] as const)
      .filter((entry): entry is readonly [string, Record<string, unknown>] =>
        Boolean(entry[0]),
      ),
  );
  const audioDescriptions = asRecordArray(encoderSettings?.AudioDescriptions);
  const audioMappings =
    audioDescriptions.length > 0
      ? audioDescriptions.map((description, index) =>
          inferAudioMapping(description, selectorByName, index),
        )
      : [{ ...DEFAULT_AUDIO_MAPPING }];

  return {
    name: asString(config?.Name) ?? channel.name ?? channel.id,
    outputMode: "manual",
    manualDestinationUrl:
      asString(firstSetting?.Url) ||
      channel.destinations.flatMap((destination) => destination.urls)[0] ||
      "",
    mediaConnectFlowArn: "",
    videoBitrate: asNumber(h264Settings?.Bitrate) ?? 15000000,
    width: asNumber(firstVideo?.Width) ?? 1920,
    height: asNumber(firstVideo?.Height) ?? 1080,
    framerateNumerator: asNumber(h264Settings?.FramerateNumerator) ?? 50,
    framerateDenominator: asNumber(h264Settings?.FramerateDenominator) ?? 1,
    audioMappings,
  };
}

function ChannelConfigDialog({
  channel,
  flows,
  isAdmin,
  open,
  onOpenChange,
}: {
  channel: AwsMediaLiveChannel | null;
  flows: AwsMediaConnectFlow[];
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const channelConfig = useMediaLiveChannelConfig(channel?.id);
  const updateConfig = useUpdateMediaLiveChannelConfig();
  const config = asRecord(channelConfig.data?.channel);
  const [form, setForm] = useState<ChannelConfigForm>({
    ...formFromChannelConfig(
      channel ?? {
        id: "",
        inputAttachments: [],
        destinations: [],
        egressEndpoints: [],
      },
      null,
    ),
  });

  useEffect(() => {
    if (!open || !channel) return;
    setForm(formFromChannelConfig(channel, config));
  }, [channel, config, open]);

  const updateAudioMapping = (
    index: number,
    mapping: Partial<AudioMappingInput>,
  ) => {
    setForm((prev) => ({
      ...prev,
      audioMappings: prev.audioMappings.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...mapping } : item,
      ),
    }));
  };

  const addAudioMapping = () => {
    setForm((prev) => ({
      ...prev,
      audioMappings: [
        ...prev.audioMappings,
        {
          selectorName: "audio_1",
          selectorType: "track",
          track: 1,
          outputName: `audio_${prev.audioMappings.length + 1}`,
          bitrate: 192000,
          sampleRate: 48000,
        },
      ],
    }));
  };

  const removeAudioMapping = (index: number) => {
    setForm((prev) => ({
      ...prev,
      audioMappings: prev.audioMappings.filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    }));
  };

  const submit = () => {
    if (!channel) return;
    updateConfig.mutate(
      {
        channelId: channel.id,
        config: {
          ...form,
          manualDestinationUrl: form.manualDestinationUrl || undefined,
          mediaConnectFlowArn: form.mediaConnectFlowArn || undefined,
        },
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const json = config ? JSON.stringify(config, null, 2) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            MediaLive Config{channel ? `: ${channel.name ?? channel.id}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[78vh] gap-5 overflow-y-auto pr-1">
          {channelConfig.isLoading ? (
            <EmptyTableMessage message="Loading channel config..." />
          ) : channelConfig.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Config unavailable</AlertTitle>
              <AlertDescription>
                {channelConfig.error instanceof Error
                  ? channelConfig.error.message
                  : "Failed to load MediaLive config"}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Channel Name">
                  <Input
                    value={form.name ?? ""}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    disabled={!isAdmin}
                  />
                </Field>
                <Field label="Output Mode">
                  <Select
                    value={form.outputMode}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        outputMode: value as ChannelConfigForm["outputMode"],
                      }))
                    }
                    disabled={!isAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual RTP/UDP URL</SelectItem>
                      <SelectItem value="mediaconnect_flow">
                        MediaConnect Flow
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {form.outputMode === "mediaconnect_flow" ? (
                <Field label="MediaConnect Flow">
                  <Select
                    value={form.mediaConnectFlowArn || "none"}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        mediaConnectFlowArn: value === "none" ? "" : value,
                      }))
                    }
                    disabled={!isAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select flow</SelectItem>
                      {flows.map((flow) => (
                        <SelectItem key={flow.arn} value={flow.arn}>
                          {flow.name} / {flow.source?.protocol ?? "source"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : (
                <Field label="Manual Destination URL">
                  <Input
                    placeholder="rtp://x.x.x.x:8001"
                    value={form.manualDestinationUrl ?? ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        manualDestinationUrl: event.target.value,
                      }))
                    }
                    disabled={!isAdmin}
                  />
                </Field>
              )}

              <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                <Field label="Width (px)">
                  <Input
                    type="number"
                    value={form.width}
                    disabled={!isAdmin}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        width: Number(event.target.value),
                      }))
                    }
                  />
                </Field>
                <Field label="Height (px)">
                  <Input
                    type="number"
                    value={form.height}
                    disabled={!isAdmin}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        height: Number(event.target.value),
                      }))
                    }
                  />
                </Field>
                <Field label="Video Bitrate (bps)">
                  <Input
                    type="number"
                    value={form.videoBitrate}
                    disabled={!isAdmin}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        videoBitrate: Number(event.target.value),
                      }))
                    }
                  />
                </Field>
                <Field label="Framerate">
                  <FramerateSelect
                    numerator={form.framerateNumerator}
                    denominator={form.framerateDenominator}
                    disabled={!isAdmin}
                    onChange={(numerator, denominator) =>
                      setForm((prev) => ({
                        ...prev,
                        framerateNumerator: numerator,
                        framerateDenominator: denominator,
                      }))
                    }
                  />
                </Field>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Audio Mappings</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addAudioMapping}
                    disabled={!isAdmin}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Mapping
                  </Button>
                </div>
                <div className="space-y-3">
                  {form.audioMappings.map((mapping, index) => (
                    <AudioMappingRow
                      key={index}
                      mapping={mapping}
                      disabled={!isAdmin}
                      canRemove={form.audioMappings.length > 1}
                      onChange={(change) => updateAudioMapping(index, change)}
                      onRemove={() => removeAudioMapping(index)}
                    />
                  ))}
                </div>
              </div>

              {!isAdmin ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Read-only config</AlertTitle>
                  <AlertDescription>
                    Employee access is required to update MediaLive config.
                  </AlertDescription>
                </Alert>
              ) : channel?.state !== "IDLE" ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Live update will be queued</AlertTitle>
                  <AlertDescription>
                    The server will stop the channel if required, apply this
                    config update, then restart it when it was running.
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-3">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard?.writeText(json)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy JSON
                  </Button>
                </div>
                <Textarea
                  readOnly
                  value={json}
                  className="min-h-[260px] font-mono text-xs"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button
                  onClick={submit}
                  disabled={
                    updateConfig.isPending ||
                    !isAdmin ||
                    !form.name ||
                    (form.outputMode === "manual" &&
                      !form.manualDestinationUrl) ||
                    (form.outputMode === "mediaconnect_flow" &&
                      !form.mediaConnectFlowArn)
                  }
                >
                  Update Config
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AudioMappingRow({
  mapping,
  disabled,
  canRemove,
  onChange,
  onRemove,
}: {
  mapping: AudioMappingInput;
  disabled?: boolean;
  canRemove: boolean;
  onChange: (mapping: Partial<AudioMappingInput>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <Field label="Selector">
          <Input
            value={mapping.selectorName}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                selectorName: event.target.value,
              })
            }
          />
        </Field>
        <Field label="Type">
          <Select
            value={mapping.selectorType}
            disabled={disabled}
            onValueChange={(value) =>
              onChange({
                selectorType: value as AudioMappingInput["selectorType"],
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="track">Track</SelectItem>
              <SelectItem value="pid">PID</SelectItem>
              <SelectItem value="language">Language</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field
          label={mapping.selectorType === "language" ? "Language" : "Track/PID"}
        >
          {mapping.selectorType === "language" ? (
            <Input
              placeholder="eng"
              value={mapping.languageCode ?? ""}
              disabled={disabled}
              onChange={(event) =>
                onChange({
                  languageCode: event.target.value,
                })
              }
            />
          ) : (
            <Input
              type="number"
              disabled={disabled}
              value={
                mapping.selectorType === "pid"
                  ? (mapping.pid ?? "")
                  : (mapping.track ?? "")
              }
              onChange={(event) =>
                onChange(
                  mapping.selectorType === "pid"
                    ? { pid: Number(event.target.value) }
                    : { track: Number(event.target.value) },
                )
              }
            />
          )}
        </Field>
        <Field label="Output Name">
          <Input
            value={mapping.outputName}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                outputName: event.target.value,
              })
            }
          />
        </Field>
        <Field label="Bitrate">
          <Input
            type="number"
            value={mapping.bitrate}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                bitrate: Number(event.target.value),
              })
            }
          />
        </Field>
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || !canRemove}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Remove
        </Button>
      </div>
    </div>
  );
}

function CreateFlowDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createFlow = useCreateMediaConnectFlow();
  const [form, setForm] = useState<CreateMediaConnectFlowInput>({
    name: "",
    sourceName: "MediaLive",
    sourceProtocol: "rtp",
    sourcePort: 8001,
    availabilityZone: "",
  });

  const submit = () => {
    createFlow.mutate(
      {
        ...form,
        availabilityZone: form.availabilityZone || undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setForm({
            name: "",
            sourceName: "MediaLive",
            sourceProtocol: "rtp",
            sourcePort: 8001,
            availabilityZone: "",
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create MediaConnect Flow</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Flow Name">
            <Input
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </Field>
          <Field label="Source Name">
            <Input
              value={form.sourceName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  sourceName: event.target.value,
                }))
              }
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Source Protocol">
              <Select
                value={form.sourceProtocol}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    sourceProtocol:
                      value as CreateMediaConnectFlowInput["sourceProtocol"],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rtp">RTP</SelectItem>
                  <SelectItem value="srt-listener">SRT Listener</SelectItem>
                  <SelectItem value="zixi-push">Zixi Push</SelectItem>
                  <SelectItem value="rist">RIST</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Source Port">
              <Input
                type="number"
                value={form.sourcePort}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    sourcePort: Number(event.target.value),
                  }))
                }
              />
            </Field>
          </div>
          <Field label="Availability Zone">
            <Input
              placeholder="eu-central-1b"
              value={form.availabilityZone}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  availabilityZone: event.target.value,
                }))
              }
            />
          </Field>
          <div className="text-sm text-muted-foreground">
            Source whitelist is fixed to 0.0.0.0/0.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={createFlow.isPending || !form.name}
            >
              Create Flow
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddOutputDialog({
  flow,
  open,
  onOpenChange,
}: {
  flow: AwsMediaConnectFlow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const addOutput = useAddMediaConnectOutput();
  const nextPort =
    flow?.outputs.reduce(
      (max, output) => Math.max(max, output.port ?? 0),
      9000,
    ) ?? 9000;
  const [output, setOutput] = useState<AddMediaConnectOutputInput["output"]>({
    name: "",
    protocol: "srt-listener",
    port: nextPort + 1,
  });
  const requiresDestination = output.protocol === "srt-caller";
  const allowsDestination = output.protocol !== "srt-listener";

  const submit = () => {
    if (!flow) return;
    const sanitizedOutput = sanitizeMediaConnectOutputSettings(output);
    addOutput.mutate(
      {
        flowArn: flow.arn,
        output: sanitizedOutput,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setOutput({
            name: "",
            protocol: "srt-listener",
            port: nextPort + 1,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Output{flow ? ` to ${flow.name}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Output Name">
            <Input
              value={output.name}
              onChange={(event) =>
                setOutput((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Protocol">
              <Select
                value={output.protocol}
                onValueChange={(value) => {
                  const protocol =
                    value as AddMediaConnectOutputInput["output"]["protocol"];
                  setOutput((prev) => ({
                    ...prev,
                    protocol,
                    destination:
                      protocol === "srt-listener"
                        ? undefined
                        : prev.destination,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="srt-listener">SRT Listener</SelectItem>
                  <SelectItem value="srt-caller">SRT Caller</SelectItem>
                  <SelectItem value="zixi-push">Zixi Push</SelectItem>
                  <SelectItem value="zixi-pull">Zixi Pull</SelectItem>
                  <SelectItem value="rist">RIST</SelectItem>
                  <SelectItem value="rtp">RTP</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Port">
              <Input
                type="number"
                value={output.port ?? ""}
                onChange={(event) =>
                  setOutput((prev) => ({
                    ...prev,
                    port: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  }))
                }
              />
            </Field>
          </div>
          {allowsDestination && (
            <Field
              label={
                output.protocol === "srt-caller"
                  ? "Destination IP"
                  : "Destination"
              }
            >
              <Input
                placeholder={
                  output.protocol === "srt-caller"
                    ? "Receiver IP address"
                    : "Destination host or IP"
                }
                value={output.destination ?? ""}
                onChange={(event) =>
                  setOutput((prev) => ({
                    ...prev,
                    destination: event.target.value || undefined,
                  }))
                }
              />
            </Field>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                addOutput.isPending ||
                !output.name ||
                !flow ||
                (requiresDestination && !output.destination)
              }
            >
              Add Output
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditOutputDialog({
  pendingOutput,
  open,
  onOpenChange,
}: {
  pendingOutput: PendingEditOutput;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateOutput = useUpdateMediaConnectOutput();
  const [output, setOutput] = useState<UpdateMediaConnectOutputInput["output"]>(
    {
      protocol: "srt-listener",
    },
  );
  const currentOutput = pendingOutput?.output;
  const flow = pendingOutput?.flow;
  const requiresDestination = output.protocol === "srt-caller";
  const allowsDestination = output.protocol !== "srt-listener";

  useEffect(() => {
    if (!currentOutput || !open) return;
    setOutput(mediaConnectOutputFormFromOutput(currentOutput));
  }, [currentOutput, open]);

  const submit = () => {
    if (!flow || !currentOutput) return;
    updateOutput.mutate(
      {
        flowArn: flow.arn,
        outputArn: currentOutput.arn,
        output: sanitizeMediaConnectOutputSettings(output),
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit Output{currentOutput ? `: ${currentOutput.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Output Name">
            <Input value={currentOutput?.name ?? ""} readOnly />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Protocol">
              <Select
                value={output.protocol}
                onValueChange={(value) => {
                  const protocol = value as MediaConnectOutputProtocol;
                  setOutput((prev) => ({
                    ...prev,
                    protocol,
                    destination:
                      protocol === "srt-listener"
                        ? undefined
                        : prev.destination,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="srt-listener">SRT Listener</SelectItem>
                  <SelectItem value="srt-caller">SRT Caller</SelectItem>
                  <SelectItem value="zixi-push">Zixi Push</SelectItem>
                  <SelectItem value="zixi-pull">Zixi Pull</SelectItem>
                  <SelectItem value="rist">RIST</SelectItem>
                  <SelectItem value="rtp">RTP</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Port">
              <Input
                type="number"
                value={output.port ?? ""}
                onChange={(event) =>
                  setOutput((prev) => ({
                    ...prev,
                    port: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  }))
                }
              />
            </Field>
          </div>
          {allowsDestination && (
            <Field
              label={
                output.protocol === "srt-caller"
                  ? "Destination IP"
                  : "Destination"
              }
            >
              <Input
                placeholder={
                  output.protocol === "srt-caller"
                    ? "Receiver IP address"
                    : "Destination host or IP"
                }
                value={output.destination ?? ""}
                onChange={(event) =>
                  setOutput((prev) => ({
                    ...prev,
                    destination: event.target.value || undefined,
                  }))
                }
              />
            </Field>
          )}
          <Field label="Stream ID">
            <Input
              value={output.streamId ?? ""}
              onChange={(event) =>
                setOutput((prev) => ({
                  ...prev,
                  streamId: event.target.value || undefined,
                }))
              }
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                updateOutput.isPending ||
                !flow ||
                !currentOutput ||
                (requiresDestination && !output.destination)
              }
            >
              Save Settings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateChannelDialog({
  open,
  onOpenChange,
  flows,
  inputs,
  inputDevices,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flows: AwsMediaConnectFlow[];
  inputs: AwsMediaLiveInput[];
  inputDevices: AwsMediaLiveInputDevice[];
}) {
  const createChannel = useCreateMediaLiveChannel();
  const [form, setForm] = useState<CreateMediaLiveChannelInput>({
    name: "",
    inputId: "",
    inputDeviceId: "",
    inputName: "",
    outputMode: "mediaconnect_flow",
    manualDestinationUrl: "",
    mediaConnectFlowArn: "",
    channelClass: "SINGLE_PIPELINE",
    videoBitrate: 15000000,
    width: 1920,
    height: 1080,
    framerateNumerator: 50,
    framerateDenominator: 1,
    audioMappings: [{ ...DEFAULT_AUDIO_MAPPING }],
  });

  const submit = () => {
    createChannel.mutate(
      {
        ...form,
        inputId: form.inputId || undefined,
        inputDeviceId: form.inputDeviceId || undefined,
        inputName: form.inputName || undefined,
        manualDestinationUrl: form.manualDestinationUrl || undefined,
        mediaConnectFlowArn: form.mediaConnectFlowArn || undefined,
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const addAudioMapping = () => {
    setForm((prev) => ({
      ...prev,
      audioMappings: [
        ...prev.audioMappings,
        {
          selectorName: "audio_1",
          selectorType: "track",
          track: 1,
          outputName: `audio_${prev.audioMappings.length + 1}`,
          bitrate: 192000,
          sampleRate: 48000,
        },
      ],
    }));
  };

  const updateAudioMapping = (
    index: number,
    mapping: Partial<AudioMappingInput>,
  ) => {
    setForm((prev) => ({
      ...prev,
      audioMappings: prev.audioMappings.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...mapping } : item,
      ),
    }));
  };

  const removeAudioMapping = (index: number) => {
    setForm((prev) => ({
      ...prev,
      audioMappings: prev.audioMappings.filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Create MediaLive Channel</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[75vh] gap-5 overflow-y-auto pr-1">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Channel Name">
              <Input
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </Field>
            <Field label="IAM Role">
              <Input value="MediaLiveAccessRole" disabled />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Existing MediaLive Input">
              <Select
                value={form.inputId || "none"}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    inputId: value === "none" ? "" : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Create from input device</SelectItem>
                  {inputs.map((input) => (
                    <SelectItem key={input.id} value={input.id}>
                      {input.name ?? input.id} / {input.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Elemental Link Device">
              <Select
                value={form.inputDeviceId || "none"}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    inputDeviceId: value === "none" ? "" : value,
                  }))
                }
                disabled={!!form.inputId}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select device</SelectItem>
                  {inputDevices.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.name ?? device.id} / {device.connectionState}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {!form.inputId && (
            <Field label="New Input Name">
              <Input
                value={form.inputName}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    inputName: event.target.value,
                  }))
                }
              />
            </Field>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Output Mode">
              <Select
                value={form.outputMode}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    outputMode:
                      value as CreateMediaLiveChannelInput["outputMode"],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mediaconnect_flow">
                    MediaConnect Flow
                  </SelectItem>
                  <SelectItem value="manual">Manual RTP/UDP URL</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.outputMode === "mediaconnect_flow" ? (
              <Field label="MediaConnect Flow">
                <Select
                  value={form.mediaConnectFlowArn || "none"}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      mediaConnectFlowArn: value === "none" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select flow</SelectItem>
                    {flows.map((flow) => (
                      <SelectItem key={flow.arn} value={flow.arn}>
                        {flow.name} / {flow.source?.protocol ?? "source"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Manual Destination URL">
                <Input
                  placeholder="rtp://x.x.x.x:8001"
                  value={form.manualDestinationUrl}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      manualDestinationUrl: event.target.value,
                    }))
                  }
                />
              </Field>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Width (px)">
              <Input
                type="number"
                value={form.width}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    width: Number(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Height (px)">
              <Input
                type="number"
                value={form.height}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    height: Number(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Video Bitrate (bps)">
              <Input
                type="number"
                value={form.videoBitrate}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    videoBitrate: Number(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Framerate">
              <FramerateSelect
                numerator={form.framerateNumerator}
                denominator={form.framerateDenominator}
                onChange={(numerator, denominator) =>
                  setForm((prev) => ({
                    ...prev,
                    framerateNumerator: numerator,
                    framerateDenominator: denominator,
                  }))
                }
              />
            </Field>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Audio Mappings</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAudioMapping}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Mapping
              </Button>
            </div>
            <div className="space-y-3">
              {form.audioMappings.map((mapping, index) => (
                <div key={index} className="rounded-md border p-3 space-y-3">
                  <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                    <Field label="Selector">
                      <Input
                        value={mapping.selectorName}
                        onChange={(event) =>
                          updateAudioMapping(index, {
                            selectorName: event.target.value,
                          })
                        }
                      />
                    </Field>
                    <Field label="Type">
                      <Select
                        value={mapping.selectorType}
                        onValueChange={(value) =>
                          updateAudioMapping(index, {
                            selectorType:
                              value as AudioMappingInput["selectorType"],
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="track">Track</SelectItem>
                          <SelectItem value="pid">PID</SelectItem>
                          <SelectItem value="language">Language</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field
                      label={
                        mapping.selectorType === "language"
                          ? "Language"
                          : "Track/PID"
                      }
                    >
                      {mapping.selectorType === "language" ? (
                        <Input
                          placeholder="eng"
                          value={mapping.languageCode ?? ""}
                          onChange={(event) =>
                            updateAudioMapping(index, {
                              languageCode: event.target.value,
                            })
                          }
                        />
                      ) : (
                        <Input
                          type="number"
                          value={
                            mapping.selectorType === "pid"
                              ? (mapping.pid ?? "")
                              : (mapping.track ?? "")
                          }
                          onChange={(event) =>
                            updateAudioMapping(
                              index,
                              mapping.selectorType === "pid"
                                ? { pid: Number(event.target.value) }
                                : { track: Number(event.target.value) },
                            )
                          }
                        />
                      )}
                    </Field>
                    <Field label="Output Name">
                      <Input
                        value={mapping.outputName}
                        onChange={(event) =>
                          updateAudioMapping(index, {
                            outputName: event.target.value,
                          })
                        }
                      />
                    </Field>
                    <Field label="Bitrate">
                      <Input
                        type="number"
                        value={mapping.bitrate}
                        onChange={(event) =>
                          updateAudioMapping(index, {
                            bitrate: Number(event.target.value),
                          })
                        }
                      />
                    </Field>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={form.audioMappings.length === 1}
                      onClick={() => removeAudioMapping(index)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Textarea
              readOnly
              value={form.audioMappings
                .map(
                  (mapping) =>
                    `${mapping.outputName} <- ${mapping.selectorName} (${mapping.selectorType})`,
                )
                .join("\n")}
              className="min-h-20 font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              createChannel.isPending ||
              !form.name ||
              (!form.inputId && !form.inputDeviceId) ||
              (form.outputMode === "mediaconnect_flow" &&
                !form.mediaConnectFlowArn) ||
              (form.outputMode === "manual" && !form.manualDestinationUrl)
            }
          >
            Create Channel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.toUpperCase();
  const isGood = [
    "ACTIVE",
    "RUNNING",
    "CONNECTED",
    "VISIBLE",
    "CONFIGURED",
  ].includes(normalized);
  const isBad = [
    "ERROR",
    "FAILED",
    "DISCONNECTED",
    "MISSING",
    "DISABLED",
  ].includes(normalized);
  return (
    <Badge
      variant={isBad ? "destructive" : isGood ? "default" : "secondary"}
      className={isGood ? "bg-green-600" : undefined}
    >
      {value}
    </Badge>
  );
}

function ConnectionHealthBadge({
  health,
}: {
  health: AwsCloudWatchConnectionHealth;
}) {
  const label =
    health.status === "NO_DATA"
      ? "No data"
      : health.status === "CONNECTED"
        ? "Connected"
        : "Disconnected";
  const timestamp = health.timestamp
    ? new Date(health.timestamp).toLocaleTimeString()
    : undefined;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={
            health.status === "DISCONNECTED"
              ? "destructive"
              : health.status === "CONNECTED"
                ? "default"
                : "secondary"
          }
          className={health.status === "CONNECTED" ? "bg-green-600" : undefined}
        >
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          {health.status === "NO_DATA" && (
            <div className="font-medium">
              No recent CloudWatch datapoints — the resource may be idle or not
              receiving a stream.
            </div>
          )}
          <div>{health.metricName}</div>
          {health.value !== undefined && <div>Value {health.value}</div>}
          {timestamp && <div>{timestamp}</div>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function IconButton({
  label,
  children,
  disabled,
  onClick,
}: {
  label: string;
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function EmptyTableMessage({ message }: { message: string }) {
  return (
    <div className="flex min-h-24 items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function FramerateSelect({
  numerator,
  denominator,
  disabled,
  onChange,
}: {
  numerator: number;
  denominator: number;
  disabled?: boolean;
  onChange: (numerator: number, denominator: number) => void;
}) {
  const value = `${numerator}/${denominator}`;
  const hasOption = FRAMERATE_OPTIONS.some(
    (option) => `${option.numerator}/${option.denominator}` === value,
  );
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        const [num, den] = next.split("/").map(Number);
        onChange(num, den);
      }}
    >
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {!hasOption && <SelectItem value={value}>{value} fps</SelectItem>}
        {FRAMERATE_OPTIONS.map((option) => (
          <SelectItem
            key={option.label}
            value={`${option.numerator}/${option.denominator}`}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
